import {
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import GridWithStateWrapper from '../GridWidthStateWrapper';
import {
  AddDashboardRequest,
  Dashboard,
  ShareDashboardRequest,
  UpdateDashboardRequest,
} from 'common/models/Dashboard/Dashboard';
import { Box, Checkbox, MenuItem, Switch } from '@mui/material';
import ActionsDropDownButton from 'components/SummaryBars/Actions/ActionsDropsDownMenu';
import { GetMyUser } from 'services/UsersService';
import { ExtendedUser } from 'common/models/Configuration/User';
import AddDashboardDialog from 'components/Dialogs/Dashboard/AddDashboardDialog';
import {
  AddNewDashboard,
  CreateDashboardKioskLink,
  DeleteDashboard,
  GetAllDashboards,
  GetDashboards,
  ShareDashboard,
  UpdateDashboard,
} from 'services/DashboardService';
import UpdateDashboardDialog from 'components/Dialogs/Dashboard/UpdateDashboardDialog';
import ConfirmationDialog from 'components/Dialogs/Generic/ConfirmationDialog';
import ShareDashboardDialog from 'components/Dialogs/Dashboard/ShareDashboardDialog';
import { Link, useNavigate } from 'react-router-dom';
import { userHasAnyPermission } from 'util/PermissionsUtils';
import { Permission as P } from 'common/models/Permissions';
import AddDashboardKioskDialog from 'components/Dialogs/Dashboard/AddDashboardKioskDialog';
import { CreateDashboardKioskLinkRequest } from 'common/models/Dashboard/DashboardKioskLink';
import DashboardKioskLinkDialog from 'components/Dialogs/Dashboard/DashboardKioskLinkDialog';

interface Props {
  gridName: string;
  setRowCountHandler?: (count: number) => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}

const defaultHiddenCols: GridColumnVisibilityModel = {
  id: false,
  isPersonal: false,
};
const linkStyle: React.CSSProperties = {
  color: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer',
};
export default function DashboardsGrid({
  setRowCountHandler,
  loadingHandler,
  errorHandler,
  successHandler,
  gridName,
}: Props) {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [rows, setRows] = useState<Dashboard[]>([]);
  const [addDashboardOpen, setAddDashboardOpen] = useState<boolean>(false);
  const [selectedDashboardId, setSelectedDashboardId] = useState<number>(0);
  const [addDashboardKioskOpen, setAddDashboardKioskOpen] = useState<boolean>(false);
  const [shareDashboardOpen, setShareDashboardOpen] = useState<boolean>(false);
  const [dashboardCopyName, setDashboardCopyName] = useState<string>('');
  const [copyDashboardOpen, setCopyDashboardOpen] = useState<boolean>(false);
  const [deleteDashboardOpen, setDeleteDashboardOpen] = useState<boolean>(false);
  const [updateDashboardOpen, setUpdateDashboardOpen] = useState<boolean>(false);
  const [openKioskLinkDialog, setOpenKioskLinkDialog] = useState<boolean>(false);
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(null);
  const [currentUser, setCurrentUser] = useState<ExtendedUser | null>(null);
  const [dashboardType, setDashboardType] = useState<'Personal' | 'All'>('Personal');
  const [generatedToken, setGeneratedToken] = useState<string>('');
  const navigate = useNavigate();
  const apiRef = useGridApiRef();

  useEffect(() => {
    const getUserData = async () => {
      const userRes = await GetMyUser();
      if (userRes) setCurrentUser(userRes);
    };
    getUserData();
  }, []);

  const getRows = async () => {
    loadingHandler && loadingHandler(true);
    let res: Dashboard[] | null = [];
    if (dashboardType == 'Personal') {
      res = await GetDashboards(errorHandler);
    } else {
      res = await GetAllDashboards(errorHandler);
    }

    if (res) {
      setRowCountHandler && setRowCountHandler(res.length);
      setRows(res);
    }
    loadingHandler && loadingHandler(false);
  };

  useEffect(() => {
    getRows();
  }, [dashboardType]);

  const openUpdateDialog = (params: GridRenderCellParams) => {
    setSelectedDashboard(params.row);
    setUpdateDashboardOpen(true);
  };

  const openCopyDashboardDialog = () => {
    const dashboardId = selectionModel[0];
    if (dashboardId) {
      const dashboard = rows.find((r) => r.id == dashboardId);
      if (dashboard) {
        setDashboardCopyName(dashboard.name + ' - Copy');
        setSelectedDashboard(dashboard);
        setCopyDashboardOpen(true);
      }
    }
  };

  const openShareDashboardDialog = (params: GridRenderCellParams) => {
    setSelectedDashboard(params.row);
    setShareDashboardOpen(true);
  };

  const handleAddKioskLink = (dashboardId: number) => {
    setAddDashboardKioskOpen(true);
    setSelectedDashboardId(dashboardId);
  };

  const columns = useMemo<GridColDef[]>(() => {
    const linkToDetails = (params: GridRenderCellParams) => {
      if (params.row.id) {
        return (
          <Link to={`/Reporting/dashboard?id=${params.row.id}`} style={linkStyle}>
            {params.value}
          </Link>
        );
      }
      return params.value;
    };

    const isPersonalRenderer = (params: GridRenderCellParams) => {
      if (params.row.isPersonal) {
        return <Checkbox checked={true} disabled={true} />;
      } else {
        return <Checkbox disabled={true} />;
      }
    };

    const isSharedRenderer = (params: GridRenderCellParams) => {
      if (params.row.shared) {
        return <Checkbox checked={true} disabled={true} />;
      } else {
        return <Checkbox disabled={true} />;
      }
    };

    const editDashboard = (params: GridRenderCellParams) => {
      if (params.row.id) {
        if (
          params.row.userId == currentUser?.userID ||
          userHasAnyPermission([P.OfficeAdministrator, P.SystemAdministrator])
        ) {
          return (
            <a
              onClick={() => {
                openUpdateDialog(params);
              }}
              style={linkStyle}
            >
              Edit
            </a>
          );
        } else {
          return params.value;
        }
      }
      return params.value;
    };

    const deleteDashboard = (params: GridRenderCellParams) => {
      if (params.row.id) {
        if (!params.row.isPersonal) {
          if (
            params.row.userId == currentUser?.userID ||
            userHasAnyPermission([P.OfficeAdministrator, P.SystemAdministrator])
          ) {
            return (
              <a
                onClick={() => {
                  setSelectedDashboard(params.row);
                  setDeleteDashboardOpen(true);
                }}
                style={linkStyle}
              >
                Delete
              </a>
            );
          } else {
            return (
              <>
                Only dashboard <strong>owners</strong> can delete dashboards.
              </>
            );
          }
        } else {
          return (
            <>
              Can&apos;t delete a <strong>Personal</strong> dashboard.
            </>
          );
        }
      }
      return params.value;
    };

    const shareWith = (params: GridRenderCellParams) => {
      if (params.row.id) {
        if (
          params.row.userId == currentUser?.userID ||
          userHasAnyPermission([P.OfficeAdministrator, P.SystemAdministrator])
        ) {
          return (
            <>
              <a
                onClick={() => {
                  openShareDashboardDialog(params);
                }}
                style={linkStyle}
              >
                Sharing Settings
              </a>
            </>
          );
        } else {
          return (
            <>
              Only dashboard <strong>owners</strong> can share dashboards.
            </>
          );
        }
      }
      return params.value;
    };

    const KioskMode = (params: GridRenderCellParams) => {
      if (params.row.id) {
        if (userHasAnyPermission([P.OfficeAdministrator, P.SystemAdministrator])) {
          return (
            <>
              <a onClick={() => handleAddKioskLink(params.row.id)} style={linkStyle}>
                Generate Kiosk Link
              </a>{' '}
              |{' '}
              <Link to="/configuration/Reporting/dashboard-kiosk-links" style={linkStyle}>
                View Kiosk Links
              </Link>
            </>
          );
        } else {
          return (
            <>
              Only an <strong>Analytics Administrator</strong> can generate kiosk links.
            </>
          );
        }
      }
      return params.value;
    };

    const cols: GridColDef[] = [
      { field: 'id', headerName: 'Id', width: 50, renderCell: linkToDetails },
      { field: 'name', headerName: 'Name', width: 300, renderCell: linkToDetails },
      { field: 'owner', headerName: 'Owner', width: 250 },
      { field: 'isPersonal', headerName: 'Personal?', width: 100, renderCell: isPersonalRenderer },
      { field: 'shared', headerName: 'Shared?', width: 150, renderCell: isSharedRenderer },
      { field: 'edit', headerName: 'Edit', renderCell: editDashboard, width: 100 },
      { field: 'shareWith', headerName: 'Share', width: 350, renderCell: shareWith },
      { field: 'kioskMode', headerName: 'Kiosk Mode', width: 350, renderCell: KioskMode },
      { field: 'delete', headerName: 'Delete', width: 350, renderCell: deleteDashboard },
    ];
    return cols;
  }, [apiRef, currentUser]);

  const gridActions = useMemo(() => {
    return (
      <ActionsDropDownButton color="primary" label="List Actions">
        <MenuItem
          onClick={() => {
            setAddDashboardOpen(true);
            setSelectedDashboard(null);
          }}
        >
          Add Dashboard
        </MenuItem>
        <MenuItem
          disabled={selectionModel.length > 0 ? false : true}
          onClick={openCopyDashboardDialog}
        >
          Copy Dashboard
        </MenuItem>
      </ActionsDropDownButton>
    );
  }, [selectionModel]);

  const AddDashboard = async (dashboardName: string) => {
    const result = await AddNewDashboard({
      name: dashboardName,
      json: JSON.stringify([]),
    } as AddDashboardRequest);
    if (result) {
      setAddDashboardOpen(false);
      navigate(`/Reporting/dashboard?id=${result}&editMode=true`);
    }
  };

  const HandleCopyDashboard = async (dashboardName: string) => {
    const result = await AddNewDashboard({
      name: dashboardName,
      json: selectedDashboard?.json,
    } as AddDashboardRequest);
    if (result) {
      setCopyDashboardOpen(false);
      navigate(`/Reporting/dashboard?id=${result}&editMode=true`);
    }
  };

  const UpdateExistingDashboard = async (dashboardName: string, userId: number) => {
    const result = await UpdateDashboard({
      id: selectedDashboard?.id,
      name: dashboardName,
      ownerId: userId,
    } as UpdateDashboardRequest);
    if (result) {
      setUpdateDashboardOpen(false);
      setSelectedDashboard(null);
      await getRows();
    }
  };

  const deleteSelectedDashboard = async () => {
    if (selectedDashboard) {
      const result = await DeleteDashboard(selectedDashboard.id);
      if (result) {
        setDeleteDashboardOpen(false);
        setSelectedDashboard(null);
        await getRows();
      }
    }
  };

  const handleDashboardShare = async (
    id: number,
    selectedUserIds: string,
    selectedGroupIds: string
  ) => {
    const result = await ShareDashboard({
      id: id,
      selectedUserIds: selectedUserIds,
      selectedGroupIds: selectedGroupIds,
    } as ShareDashboardRequest);
    if (result) {
      successHandler && successHandler('Sharing settings updated');
      getRows();
      setShareDashboardOpen(false);
      setSelectedDashboard(null);
    }
  };

  const dashboardTypeChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    const value = isChecked ? 'All' : 'Personal';
    setDashboardType(value);
  }, []);

  const allDashboardsSwitch = useMemo(
    () => (
      <span style={{ marginRight: '20px' }}>
        <Switch checked={dashboardType === 'All'} onChange={dashboardTypeChangeHandler} />
        <span>All Dashboards</span>
      </span>
    ),
    [dashboardType, dashboardTypeChangeHandler]
  );

  const handleAddDashboardKioskLink = async (request: CreateDashboardKioskLinkRequest) => {
    const res = await CreateDashboardKioskLink(request);
    if (res) {
      successHandler && successHandler('Kiosk Link created');
      const { protocol, host } = window.location;
      setGeneratedToken(`${protocol}//${host}/kiosk-dashboard/${res}`);
      setOpenKioskLinkDialog(true);
    } else {
      errorHandler && errorHandler('Error creating Kiosk Link');
    }
  };

  return (
    <>
      <AddDashboardKioskDialog
        addKioskLink={handleAddDashboardKioskLink}
        open={addDashboardKioskOpen}
        closeHandler={() => setAddDashboardKioskOpen(false)}
        dashboardId={selectedDashboardId}
        errorHandler={errorHandler}
      />
      <ConfirmationDialog
        open={deleteDashboardOpen}
        message="Are you sure you want to delete the dashboard?"
        onClose={() => setDeleteDashboardOpen(false)}
        onContinue={deleteSelectedDashboard}
        title="Confirm action"
      />
      <AddDashboardDialog
        open={addDashboardOpen}
        closeHandler={() => {
          setAddDashboardOpen(false);
        }}
        submitHandler={AddDashboard}
      />

      <AddDashboardDialog
        open={copyDashboardOpen}
        name={dashboardCopyName ?? ''}
        closeHandler={() => {
          setCopyDashboardOpen(false);
          setDashboardCopyName('');
          setSelectedDashboard(null);
        }}
        submitHandler={HandleCopyDashboard}
      />

      <UpdateDashboardDialog
        name={selectedDashboard?.name ?? ''}
        userId={currentUser?.userID ?? 0}
        open={updateDashboardOpen}
        closeHandler={() => {
          setUpdateDashboardOpen(false);
        }}
        submitHandler={UpdateExistingDashboard}
      />

      <ShareDashboardDialog
        open={shareDashboardOpen}
        closeDialogHandler={() => {
          setShareDashboardOpen(false);
          setSelectedDashboard(null);
        }}
        data={selectedDashboard}
        shareDashboardHandler={handleDashboardShare}
      />

      <DashboardKioskLinkDialog
        open={openKioskLinkDialog}
        closeHandler={() => setOpenKioskLinkDialog(false)}
        token={generatedToken}
      />

      {Boolean(gridActions) && (
        <Box pb="10px" ml="auto">
          {userHasAnyPermission([P.OfficeAdministrator, P.SystemAdministrator]) &&
            allDashboardsSwitch}
          {gridActions}
        </Box>
      )}
      <GridWithStateWrapper
        rows={rows}
        columns={columns}
        apiRef={apiRef}
        checkboxSelection={true}
        disableRowSelectionOnClick={false}
        disableMultipleRowSelection={true}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(sm) => setSelectionModel(sm)}
        pagination
        pageSizeOptions={[100, 250, 500, 1000]}
        gridName={gridName}
        defaultViewModel={defaultHiddenCols}
      />
    </>
  );
}
